/* eslint-disable  @typescript-eslint/no-explicit-any */

import { PayloadAction } from '@reduxjs/toolkit';
import { AuthUtils } from '@tecma/auth-utils';
import i18n from '@tecma/i18n';
import { logger } from '@tecma/logs-fe';
import Cookies from 'js-cookie';
import { DateTime } from 'luxon';
import { all, call, delay, put, select, takeLatest } from 'redux-saga/effects';

import { authActions } from 'store/sagas/application/auth/auth.actions';
import { applicationStateSelectors } from 'store/slices/applicationState/selectors';
import { applicationStateActions } from 'store/slices/applicationState/slice';
import { setImpersonatedUser, setLoggedUser, setToken, unsetLoggedUser } from 'store/slices/auth/slice';
import { environmentSelectors } from 'store/slices/environment/selector';
import { EnvironmentState } from 'store/slices/environment/slice';
import { InitAuthSaga } from 'store/types/auth';

import { getBackUrl, setTokenCookies } from 'utils/auth';

import { getClient } from './auth.api';
import {
  LOGOUT_COOKIE,
  JWT_TOKEN_COOKIE_KEY,
  REFRESH_TOKEN_COOKIE_KEY,
  EXPIRES_IN_COOKIE_KEY,
  IMPERSONATED_CLIENT_COOKIE,
} from '../../../../constants';

function* getUserInfo(action: PayloadAction<string>): any {
  const environment: EnvironmentState = yield select(environmentSelectors.environment);
  const userInfo = yield call(AuthUtils.getUserByJWT, action.payload, environment.REACT_APP_AWS_TOKEN);
  yield put(setLoggedUser(userInfo));
  return userInfo;
}

export function* initAuth(action: PayloadAction<InitAuthSaga>): any {
  const { user, token, projectId } = action.payload;
  const logout = Cookies.get(LOGOUT_COOKIE);
  if (logout === 'true' && user) {
    yield put(authActions.doLogout());
  }
  const hasAllCookies = Cookies.get(JWT_TOKEN_COOKIE_KEY) && Cookies.get(REFRESH_TOKEN_COOKIE_KEY) && Cookies.get(EXPIRES_IN_COOKIE_KEY);

  if (hasAllCookies && (!token || !user)) {
    const newToken: IToken = {
      accessToken: Cookies.get(JWT_TOKEN_COOKIE_KEY) ?? '',
      refreshToken: Cookies.get(REFRESH_TOKEN_COOKIE_KEY) ?? '',
      expiresIn: Cookies.get(EXPIRES_IN_COOKIE_KEY) ?? '',
    };
    yield put(setToken(newToken));
    setTokenCookies(newToken);
    try {
      yield call(getUserInfo, {
        payload: projectId,
        type: 'auth/getUserByJWT',
      });
      /* const userInfo = yield call(AuthUtils.getUserByJWT); */
      if (Cookies.get(IMPERSONATED_CLIENT_COOKIE)) {
        try {
          const clientInfo = yield getClient(projectId);
          yield put(setImpersonatedUser({ ...clientInfo, client: clientInfo.id }));
        } catch (e) {
          logger.error(e);
        }
      }
      yield all([put(applicationStateActions.applicationInitDone()), put(applicationStateActions.loadingStopRequired())]);
    } catch (error) {
      logger.error(error);
      yield put(authActions.doLogout());
    }
  } else {
    yield put(authActions.doLogout());
  }
}

export const getCurrentEnvFromUrl = (url: string): string => {
  if (url.toLowerCase().includes('dev1') || url.toLowerCase().includes('localhost')) {
    return 'dev1';
  }
  if (url.toLowerCase().includes('test')) {
    return 'test';
  }
  if (url.toLowerCase().includes('pre-prod')) {
    return 'pre-prod';
  }
  if (url.toLowerCase().includes('demo-prod')) {
    return 'demo-prod';
  }
  return 'prod';
};

export const getAuthConfiguration = (
  projectId: string,
  redirectUrl: string,
  platform: string,
  locale: string,
  isConfigurationComplete?: boolean,
  redirectToSignUp?: boolean,
) => {
  const currentEnv = getCurrentEnvFromUrl(redirectUrl);
  const queryParams = {
    projectId,
    redirectUrl,
    platform,
    locale,
    ...(isConfigurationComplete ? { isConfigurationComplete: `${isConfigurationComplete}` } : {}),
    ...(redirectToSignUp ? { redirectToSignUp: `${redirectToSignUp}` } : {}),
  };
  return { queryParams, currentEnv };
};

export const generateUrlToMyAccount = (
  env: string,
  myAccountRoutePath: string,
  queryParams: {
    redirectUrl: string;
    projectId: string;
    platform: string;
    locale: string;
    isConfigurationComplete?: string;
    redirectToSignUp?: string;
  },
  myAccountBaseUrl: string,
): string => {
  const locationRedirectUrl = new URL(queryParams.redirectUrl ?? '');
  const baseMyAccountURL = locationRedirectUrl.hostname === 'localhost' ? 'http://localhost:4000' : myAccountBaseUrl;
  const finalUrl = new URL(`${baseMyAccountURL}${myAccountRoutePath}`);
  
  Object.entries(queryParams).forEach(([key, value]) => {
    if (value !== undefined) {
      const decodedValue = decodeURIComponent(value);
      
      if (decodedValue.includes('&') && decodedValue.includes('=')) {
        const parts = decodedValue.split('&');
        const mainValue = parts[0];
        const additionalParams = parts.slice(1);
        
        finalUrl.searchParams.set(key, mainValue);
        
        additionalParams.forEach((param) => {
          const [paramKey, paramValue] = param.split('=');
          if (paramKey && paramValue !== undefined) {
            finalUrl.searchParams.set(paramKey, paramValue);
          }
        });
      } else {
        finalUrl.searchParams.set(key, decodedValue);
      }
    }
  });
  return decodeURIComponent(finalUrl.toString());
};
const authenticate = ({
  projectId,
  redirectUrl,
  platform,
  locale,
  myAccountBaseUrl,
  isConfigurationComplete,
  redirectToSignUp,
}: {
  projectId: string;
  redirectUrl: string;
  platform: string;
  locale: string;
  myAccountBaseUrl: string;
  isConfigurationComplete?: boolean;
  redirectToSignUp?: boolean;
}) => {
  const { currentEnv, queryParams } = getAuthConfiguration(
    projectId,
    redirectUrl,
    platform,
    locale,
    isConfigurationComplete,
    redirectToSignUp,
  );
  const urlToMyAccount = generateUrlToMyAccount(currentEnv, redirectToSignUp ? '/signUp' : '/signIn', queryParams, myAccountBaseUrl);
  console.log('urlToMyAccount', urlToMyAccount);
};

function* doLogin(): any {
  const projectId = yield select(applicationStateSelectors.selectProjectId);
  const myAccount = yield select(applicationStateSelectors.selectMyAccount);

  authenticate({
    projectId,
    redirectUrl: getBackUrl(),
    platform: 'MYHOME',
    locale: i18n.language,
    myAccountBaseUrl: myAccount?.url,
  });
  AuthUtils.authenticate({
    projectId,
    redirectUrl: getBackUrl(),
    platform: 'MYHOME',
    locale: i18n.language,
    myAccountBaseUrl: myAccount?.url,
  });
}

function* doLogout(): any {
  // yield put(authActions.doLogin());
  yield put(applicationStateActions.loadingRequired());
  AuthUtils.logout();
  yield all([put(unsetLoggedUser()), put(authActions.doLogin())]);
}

function* getNewToken(timeout: number, baseTimeout: number, token: IToken, user: IUser): any {
  const logout = Cookies.get(LOGOUT_COOKIE);
  const { refreshToken: newRefreshToken, expiresIn } = token;
  const environment: EnvironmentState = yield select(environmentSelectors.environment);

  try {
    if (logout !== 'true' && user && baseTimeout >= 0 && newRefreshToken && expiresIn === Cookies.get(EXPIRES_IN_COOKIE_KEY)) {
      try {
        const response = yield call(AuthUtils.refreshToken, newRefreshToken, user.email, environment.REACT_APP_AWS_TOKEN);
        if (response) {
          const refreshedToken: IToken = response;
          setTokenCookies(refreshedToken);
          yield put(setToken(refreshedToken));
        }
      } catch (error) {
        yield put(authActions.doLogout());
      }
    } else if (logout !== 'true' && baseTimeout > 0 && expiresIn !== Cookies.get(EXPIRES_IN_COOKIE_KEY)) {
      yield setNewCookies();
    } else if (baseTimeout < 0 || logout === 'true') {
      yield put(authActions.doLogout());
    }
  } catch (e) {
    logger.error(e);
    yield delay(timeout);
    yield getNewToken(timeout, baseTimeout, token, user);
  }
}

function* setNewCookies() {
  if (Cookies.get(JWT_TOKEN_COOKIE_KEY) && Cookies.get(REFRESH_TOKEN_COOKIE_KEY) && Cookies.get(EXPIRES_IN_COOKIE_KEY)) {
    const token: IToken = {
      accessToken: Cookies.get(JWT_TOKEN_COOKIE_KEY) ?? '',
      refreshToken: Cookies.get(REFRESH_TOKEN_COOKIE_KEY) ?? '',
      expiresIn: Cookies.get(EXPIRES_IN_COOKIE_KEY) ?? '',
    };
    yield put(setToken(token));
  }
}

export function* startCountDown(action: PayloadAction<LoginByProjectData>) {
  const { token, user } = action.payload;
  const environment: EnvironmentState = yield select(environmentSelectors.environment);
  const targetTime = DateTime.fromISO(token.expiresIn).toMillis() - Number(environment.REACT_APP_TOKEN_EXPIRATION_THRESHOLD);
  const baseTimeout = targetTime - Date.now();
  const maxTimeoutValue = 2147483647;
  const timeout = baseTimeout > maxTimeoutValue ? maxTimeoutValue : baseTimeout;

  if (timeout > 0) {
    yield delay(timeout);
    if (Cookies.get(LOGOUT_COOKIE) !== 'true') {
      yield getNewToken(timeout, baseTimeout, token, user);
    }
  } else if (token && token.accessToken && token.expiresIn) {
    if (DateTime.fromISO(token.expiresIn) < DateTime.now()) {
      yield put(authActions.doLogout());
    }
  }
}

export default function* authSaga() {
  yield takeLatest(authActions.initAuth, initAuth);
  yield takeLatest(authActions.getUserByJWT, getUserInfo);
  yield takeLatest(authActions.doLogin, doLogin);
  yield takeLatest(authActions.doLogout, doLogout);
  yield takeLatest(authActions.startCountDown, startCountDown);
}
